/**
 * Zustand store for Ad Slots — flexible ad placement across the storefront.
 * Replaces src/data/adSlotsStore.ts (which now re-exports from here).
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getCurrentTenantId } from "@/lib/tenantContext";

const AD_SLOTS_KEY = "khays_ad_slots";

export type AdSlotPosition =
  | "before-hero"
  | "after-hero"
  | "before-trust"
  | "after-trust"
  | "before-featured"
  | "after-featured"
  | "before-form"
  | "after-form"
  | "before-faq"
  | "after-faq"
  | "before-footer"
  | "inline-form-top"
  | "inline-form-bottom";

export const AD_SLOT_LABELS: Record<AdSlotPosition, string> = {
  "before-hero": "Before Hero",
  "after-hero": "After Hero",
  "before-trust": "Before Trust Badges",
  "after-trust": "After Trust Badges",
  "before-featured": "Before Featured Device",
  "after-featured": "After Featured Device",
  "before-form": "Before Order Form",
  "after-form": "After Order Form",
  "before-faq": "Before FAQ",
  "after-faq": "After FAQ",
  "before-footer": "Before Footer",
  "inline-form-top": "Inside Form (Top)",
  "inline-form-bottom": "Inside Form (Bottom)",
};

export type AdAudience = "all" | "main" | "tenants";

export interface AdSlot {
  id: string;
  label: string;
  imageUrl: string;
  linkUrl: string;
  position: AdSlotPosition;
  enabled: boolean;
  order: number;
  /** Audience scope: "all" (default), "main" = main storefront only, "tenants" = tenant storefronts only. */
  audience?: AdAudience;
}

function loadFromStorage(): AdSlot[] {
  try {
    const stored = localStorage.getItem(AD_SLOTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

interface AdSlotsState {
  slots: AdSlot[];
  _rev: number;
  setSlots: (slots: AdSlot[]) => void;
  reload: () => void;
}

export const useAdSlotsStore = create<AdSlotsState>()(
  devtools(
    (set, get) => ({
      slots: loadFromStorage(),
      _rev: 0,
      setSlots: (slots) => {
        localStorage.setItem(AD_SLOTS_KEY, JSON.stringify(slots));
        set({ slots, _rev: get()._rev + 1 });
      },
      reload: () => set({ slots: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "AdSlotsStore" }
  )
);

// ─── Functional API ────────────────────────────────────────────

export function loadAdSlots(): AdSlot[] {
  return useAdSlotsStore.getState().slots;
}

export function saveAdSlots(slots: AdSlot[]): void {
  useAdSlotsStore.getState().setSlots(slots);
}

export function addAdSlot(slot: Omit<AdSlot, "id" | "order">): AdSlot {
  const slots = useAdSlotsStore.getState().slots;
  const newSlot: AdSlot = {
    ...slot,
    id: `ad_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    order: slots.filter(s => s.position === slot.position).length,
  };
  useAdSlotsStore.getState().setSlots([...slots, newSlot]);
  return newSlot;
}

export function updateAdSlot(id: string, updates: Partial<AdSlot>): void {
  const slots = useAdSlotsStore.getState().slots.map(s =>
    s.id === id ? { ...s, ...updates } : s
  );
  useAdSlotsStore.getState().setSlots(slots);
}

export function removeAdSlot(id: string): void {
  const slots = useAdSlotsStore.getState().slots.filter(s => s.id !== id);
  useAdSlotsStore.getState().setSlots(slots);
}

export function getAdsForPosition(position: AdSlotPosition): AdSlot[] {
  let tenantId: string | null = null;
  try { tenantId = getCurrentTenantId(); } catch {}
  return useAdSlotsStore.getState().slots
    .filter(s => s.position === position && s.enabled)
    .filter(s => {
      const aud = s.audience || "all";
      if (aud === "all") return true;
      if (aud === "main") return tenantId === null;
      if (aud === "tenants") return tenantId !== null;
      return true;
    })
    .sort((a, b) => a.order - b.order);
}

export function reorderAdSlots(ids: string[]): void {
  const slots = [...useAdSlotsStore.getState().slots];
  ids.forEach((id, i) => {
    const slot = slots.find(s => s.id === id);
    if (slot) slot.order = i;
  });
  useAdSlotsStore.getState().setSlots(slots);
}
